/**
 * Client-side absolute playback clock — interpolate from server anchors.
 * Progress never depends on <audio>.currentTime or Discord metrics.
 */
export interface ClockSnapshot {
  anchorPositionSec: number;
  anchorServerTimeMs: number;
  isPaused: boolean;
  timelineRevision: number;
  durationSec?: number | null;
  serverTimeMs?: number;
}

export interface ClockOffsetSample {
  clientSendMs: number;
  serverTimeMs: number;
  clientRecvMs: number;
}

/** Estimate server−client offset using RTT midpoint. */
export function estimateClockOffsetMs(sample: ClockOffsetSample): number {
  const mid = (sample.clientSendMs + sample.clientRecvMs) / 2;
  return sample.serverTimeMs - mid;
}

export function mergeClockOffset(
  prevOffsetMs: number | null,
  sample: ClockOffsetSample,
  prevRttMs: number | null,
): { offsetMs: number; rttMs: number } {
  const rtt = Math.max(0, sample.clientRecvMs - sample.clientSendMs);
  const offset = estimateClockOffsetMs(sample);
  if (prevOffsetMs == null || prevRttMs == null || rtt <= prevRttMs) {
    return { offsetMs: offset, rttMs: rtt };
  }
  // Keep previous better sample; blend gently toward new.
  return {
    offsetMs: prevOffsetMs * 0.8 + offset * 0.2,
    rttMs: prevRttMs,
  };
}

export function interpolatePosition(
  snap: ClockSnapshot,
  nowClientMs: number,
  clockOffsetMs: number,
): number {
  const serverNow = nowClientMs + clockOffsetMs;
  if (snap.isPaused) return Math.max(0, snap.anchorPositionSec);
  const elapsed = Math.max(0, (serverNow - snap.anchorServerTimeMs) / 1000);
  let pos = snap.anchorPositionSec + elapsed;
  if (snap.durationSec != null && Number.isFinite(snap.durationSec) && snap.durationSec > 0) {
    pos = Math.min(pos, snap.durationSec);
  }
  return Math.max(0, pos);
}

/**
 * While playing within the same timeline revision, never rewind from ordinary
 * snapshot noise. Explicit seeks bump timelineRevision.
 */
export function resolveDisplayedPosition(opts: {
  prevDisplaySec: number;
  nextSec: number;
  prevTimelineRevision: number;
  nextTimelineRevision: number;
  isPaused: boolean;
}): number {
  if (opts.isPaused) return opts.nextSec;
  if (opts.nextTimelineRevision !== opts.prevTimelineRevision) return opts.nextSec;
  return Math.max(opts.prevDisplaySec, opts.nextSec);
}

/** Hard-seek audio only on discontinuities or material drift; never change rate. */
export function shouldHardSeekAudio(opts: {
  elementSec: number;
  targetSec: number;
  timelineChanged: boolean;
  hardSeekThresholdSec?: number;
}): boolean {
  if (opts.timelineChanged) return true;
  const threshold = opts.hardSeekThresholdSec ?? 1.0;
  return Math.abs(opts.elementSec - opts.targetSec) >= threshold;
}
